/**
 * Set-password modal.
 *
 * Shown in two contexts:
 *  1. After a magic-link sign-in for a user whose profile.password_set
 *     is still false — dismissable with a "Skip for now" button that
 *     records a localStorage flag (one device).
 *  2. During a PASSWORD_RECOVERY flow — non-dismissable; user MUST
 *     set a new password before they can use the app.
 */
import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { PW_DISMISS_KEY } from "../config";
import { toast } from "../utils/toast";
import { restPatch, restUpdatePassword } from "../utils/withRetry";

interface Props {
  open: boolean;
  recovery: boolean;
  onClose: () => void;
}

function strengthScore(pw: string): number {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/[0-9]/.test(pw)) classes++;
  if (/[^A-Za-z0-9]/.test(pw)) classes++;
  if (classes >= 2) s++;
  if (classes >= 3) s++;
  return Math.min(s, 4);
}

const STRENGTH_LABEL = ["Too weak", "Weak", "Fair", "Good", "Strong"];

export function SetPasswordModal({ open, recovery, onClose }: Props): JSX.Element | null {
  const [pw, setPw] = useState("");
  const [pwConf, setPwConf] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const score = strengthScore(pw);
  const lengthOk = pw.length >= 10;
  // bcrypt — Supabase Auth's password hasher — has a hard 72-BYTE
  // limit. With UTF-8, accented characters (à, è, ù, ò, ç…) count as
  // 2 bytes; emoji count as 4. Italian users picking a long passphrase
  // with accents bump into this fast. We check on the client so we can
  // show a clear message instead of a generic server rejection (or a
  // silent-truncate scenario where the user's password works the first
  // time but not the second). TextEncoder gives the real byte length.
  const pwBytes = pw ? new TextEncoder().encode(pw).length : 0;
  const bytesOk = pwBytes <= 72;
  const matches = pw && pwConf && pw === pwConf;
  const canSave = lengthOk && bytesOk && matches && score >= 2;

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    try {
      // Step 1: hard-required — the actual password change.
      //
      // restUpdatePassword does a raw PUT /auth/v1/user with the JWT
      // from localStorage, bypassing the SDK's navigator.locks
      // contention with the realtime channel. Before this fix, users
      // who tried to set a password right after a magic-link sign-in
      // could hit a 12-second timeout (the SDK acquired-then-stalled
      // on the lock) — Daniele Di Fini's "can't set password" bug.
      // Built-in AbortController timeout on the fetch so the UI always
      // recovers cleanly.
      await restUpdatePassword(pw);

      // Step 2: best-effort — flip the profile flag so we don't
      // re-prompt this user on subsequent sign-ins. restPatch uses
      // the same raw-fetch bypass; if it fails (RLS hiccup, transient
      // network), the worst case is the modal opens once more next
      // time and the user dismisses it again.
      if (state.value.profileId) {
        try {
          await restPatch(
            "profiles",
            `id=eq.${state.value.profileId}`,
            { password_set: true },
          );
        } catch (pErr) {
          console.warn("password_set flag update failed:", pErr);
        }
      }

      try {
        sessionStorage.removeItem(PW_DISMISS_KEY);
        localStorage.removeItem(PW_DISMISS_KEY); // clear any legacy flag too
      } catch { /* ignore */ }
      toast("Password saved — sign in directly next time.");
      onClose();
    } catch (err) {
      console.error("updateUser(password) failed:", err);
      const msg = err instanceof Error ? err.message : "Could not save password";
      // Supabase Auth returns "422" on bcrypt-overflow rejections. If
      // we somehow get past our client-side byte guard (e.g. paste +
      // submit in <1 frame), surface a clearer error than "HTTP 422".
      if (/HTTP 422/.test(msg) || /password/i.test(msg)) {
        toast(
          "Password rejected by server. Try a shorter one (under 72 bytes — accented characters count as 2).",
        );
      } else {
        toast(msg);
      }
      setBusy(false);
    }
  };

  const skip = (): void => {
    // Session-scoped (see config.ts): the prompt returns on the next
    // sign-in because the user still has no password set.
    try { sessionStorage.setItem(PW_DISMISS_KEY, "1"); } catch { /* ignore */ }
    onClose();
  };

  return (
    <div class="modal-backdrop open" role="presentation">
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="setPwTitle" tabindex={-1}>
        <h3 id="setPwTitle">{recovery ? "Set a new password" : "Set a password"}</h3>
        <p class="modal-intro">
          {recovery
            ? "Pick a new password to finish the recovery process."
            : "You signed in via email link. Pick a password now to skip the inbox round-trip next time. You can change it later via the “Forgot your password?” link."}
        </p>

        <label for="newPassword">New password</label>
        <input
          id="newPassword"
          type="password"
          autocomplete="new-password"
          minlength={10}
          placeholder="At least 10 characters"
          value={pw}
          onInput={(e) => setPw((e.target as HTMLInputElement).value)}
        />

        <div class="pw-strength" aria-live="polite">
          <div class="pw-strength-bar">
            <div class={`pw-strength-fill s-${["", "weak", "fair", "good", "strong"][score]}`} />
          </div>
          <div class="pw-strength-label">
            {pw ? STRENGTH_LABEL[score] : "Type a password to see its strength"}
            {pw && (
              <span style={`margin-left:8px;color:${bytesOk ? "var(--muted)" : "var(--s-out)"}`}>
                · {pwBytes}/72 bytes{!bytesOk ? " — too long" : ""}
              </span>
            )}
          </div>
        </div>

        <label for="newPasswordConfirm">Confirm password</label>
        <input
          id="newPasswordConfirm"
          type="password"
          autocomplete="new-password"
          placeholder="Type it again"
          value={pwConf}
          onInput={(e) => setPwConf((e.target as HTMLInputElement).value)}
        />
        {pwConf && pw !== pwConf && (
          <div class="pw-mismatch">The two passwords don't match.</div>
        )}

        <div class="modal-actions">
          {!recovery && (
            <button type="button" class="modal-secondary" onClick={skip}>
              Skip for now
            </button>
          )}
          <button type="button" class="modal-primary" disabled={!canSave || busy} onClick={save}>
            {busy ? "Saving…" : "Save password"}
          </button>
        </div>
      </div>
    </div>
  );
}
